import React, { type FC } from 'react';
import { observer } from 'mobx-react-lite';
import { Form, Tag, TagProps, Tooltip } from 'antd';
import { DatePicker, Space } from '@g2u/ui-kit';
import { CalendarOutlined } from '@ant-design/icons';
import {
  addHours,
  format,
  getHours,
  getMinutes,
  getSeconds,
  isAfter,
  isBefore,
  isToday,
  parse,
  startOfDay,
  differenceInMinutes,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

import { JsonDataItem, type WithOptionalDataTest } from 'types';
import styles from './task-due-date.module.scss';

export namespace TaskDueDate {
  export type Props = {
    record: JsonDataItem;
    text: string;
    onDateUpdate: (data: JsonDataItem) => void;
  } & WithOptionalDataTest;

  const getFormattedDuration = (from: Date, to: Date) => {
    const totalMinutes = differenceInMinutes(to, from);
    const absMinutes = Math.abs(totalMinutes);
    const days = Math.floor(absMinutes / (60 * 24));
    const hours = Math.floor((absMinutes % (60 * 24)) / 60);
    const minutes = absMinutes % 60;

    const formatTime = (value: number, unit: string) => (value > 0 ? `${value}${unit} ` : '');

    return (formatTime(days, 'd') + formatTime(hours, 'h') + formatTime(minutes, 'm')).trim();
  };

  export const $: FC<Props> = observer((props) => {
    const { record, text, onDateUpdate } = props;

    const datesBeforeNow = (current: Date | null) => {
      return !!current && isBefore(startOfDay(current), startOfDay(new Date()));
    };

    const timeBeforeNow = (current: Date | null) => {
      if (!current || !isToday(current)) {
        return {};
      }

      const now = new Date();
      const currentHour = getHours(now);
      const currentMinute = getMinutes(now);
      const currentSecond = getSeconds(now);

      return {
        disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter((hour) => hour < currentHour),
        disabledMinutes: () =>
          Array.from({ length: 60 }, (_, i) => i).filter(
            (minute) => minute <= currentMinute && getHours(current) === currentHour,
          ),
        disabledSeconds: () =>
          Array.from({ length: 60 }, (_, i) => i).filter(
            (second) =>
              second <= currentSecond && getMinutes(current) === currentMinute && getHours(current) === currentHour,
          ),
      };
    };

    const getStatusTag = (due_date: string, archive_date?: string) => {
      const now = toZonedTime(new Date(), 'Australia/Sydney');
      const dueDate = parse(due_date, 'yyyy-MM-dd HH:mm:ss', now);

      if (archive_date) {
        return (
          <Tag color="green">
            Closed on {format(parse(archive_date, 'yyyy-MM-dd HH:mm:ss', new Date()), 'dd/MM/yyyy')}
          </Tag>
        );
      }

      const formattedDuration = getFormattedDuration(now, dueDate);

      if (isAfter(now, dueDate)) {
        return <Tag color="error">Overdue - {formattedDuration}</Tag>;
      } else {
        const color: TagProps['color'] = isBefore(dueDate, addHours(now, 1)) ? 'warning' : 'blue';
        return <Tag color={color}>Due - {formattedDuration}</Tag>;
      }
    };

    const handleDateChange = (data: JsonDataItem) => {
      onDateUpdate({
        taskId: record.ID,
        date: format(new Date(data.date), 'yyyy-MM-dd HH:mm'),
      });
    };

    const isClosed = !!record.archive_datetime;
    const isInvalid = text === '0000-00-00 00:00:00';
    return (
      <Space.$ direction={Space.Direction.Vertical}>
        <Form onValuesChange={handleDateChange}>
          {isInvalid ? (
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
              {isClosed ? (
                getStatusTag(text, record.archive_datetime)
              ) : (
                <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.ExtraSmall}>
                  <Tag color="blue">New</Tag>
                  <Tooltip title="Update due date ">
                    <div>
                      <DatePicker.$
                        name={'date'}
                        disabled={isClosed}
                        icon={(<CalendarOutlined className={styles.taskDueDate__icon} onClick={() => null} />) as any}
                        disabledDate={datesBeforeNow}
                        disabledTime={timeBeforeNow}
                        format="dd-MM-yyyy HH:mm"
                        allowClear={false}
                        iconMode
                        showTime
                      />
                    </div>
                  </Tooltip>
                </Space.$>
              )}
            </Space.$>
          ) : (
            <Space.$ direction={Space.Direction.Vertical} size={Space.Size.ExtraSmall}>
              <Space.$ direction={Space.Direction.Horizontal} size={Space.Size.Small}>
                {format(parse(text, 'yyyy-MM-dd HH:mm:ss', new Date()), isClosed ? 'dd MMM yyyy' : 'dd MMM yyyy HH:mm')}
                <Tooltip title="Update due date ">
                  <div>
                    <DatePicker.$
                      name={'date'}
                      disabled={isClosed}
                      icon={<CalendarOutlined className={styles.taskDueDate__icon} onClick={() => null} />}
                      disabledDate={datesBeforeNow}
                      disabledTime={timeBeforeNow}
                      format="dd-MM-yyyy HH:mm"
                      allowClear={false}
                      iconMode
                      showTime
                    />
                  </div>
                </Tooltip>
              </Space.$>
              {getStatusTag(text, record.archive_datetime)}
            </Space.$>
          )}
        </Form>
      </Space.$>
    );
  });
}
